import React, { useState, useEffect } from 'react';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    TextField,
    IconButton,
    Button,
    Grid,
    TableCell,
    TableContainer,
    Table,
    TableRow,
    TableBody,
    Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import axiosVendorInstance from '../../axiosVendorInstance';
import MobileBackIcon from '../../assets/svg/MobileBackIcon';
import VendorTopMenuBar from './VendorLayouts/VendorTopMenuBar';
import VendorSideMenuBar from './VendorLayouts/VendorSideMenuBar';
import Loader from '../../Components/Loader/Loader';
import { useCart } from '../../CartProvider';
import { toast } from 'react-toastify';
import TimezoneSelect from 'react-timezone-select';
import '../User/profilepage/Profilesettings.css';
import { styled } from '@mui/material/styles';
import timezonePlugin from 'dayjs/plugin/timezone';
dayjs.extend(timezonePlugin);

const BusinessHours = () => {
    const days = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
    ];

    const [hours, setHours] = useState(
        days.map((day) => ({
            day,
            enabled: false,
            slots: [{ from: null, to: null }],
        }))
    );

    const [timezone, setTimezone] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    const [isUpdating, setIsUpdating] = useState(false);
    const [loading, setLoading] = useState(false);
    const { currentVendorId } = useCart();

    useEffect(() => {
        const fetchBusinessHours = async () => {
            setLoading(true);
            try {
                const response = await axiosVendorInstance.get(
                    `/business-hours/vendor/${currentVendorId}`
                );
                if (response?.data && response?.data?.business_hours?.length > 0) {
                    const tzFromDB = response?.data?.timezone || timezone;
                    setTimezone(tzFromDB);

                    const updatedHours = days.map((day) => {
                        const matched = response?.data?.business_hours?.find(
                            (h) => h.day === day
                        );

                        const isOpen = matched?.is_open ?? false;
                        const intervals = matched?.intervals || [];

                        return {
                            day,
                            enabled: isOpen,
                            slots: isOpen
                                ? intervals.map((interval) => ({
                                    from: interval.open_time
                                        ? dayjs.tz(`1970-01-01T${interval.open_time}`, tzFromDB)
                                        : null,
                                    to: interval.close_time
                                        ? dayjs.tz(`1970-01-01T${interval.close_time}`, tzFromDB)
                                        : null,
                                }))
                                : [{ from: null, to: null }],
                        };
                    });

                    setHours(updatedHours);
                    setIsUpdating(true);
                }
            } catch (error) {
                console.error('Error fetching business hours:', error);
            } finally {
                setLoading(false);
            }
        };


        fetchBusinessHours();
    }, []);

    const toggleDay = (index) => {
        const updated = [...hours];
        updated[index].enabled = !updated[index].enabled;
        if (!updated[index].enabled) {
            updated[index].slots = [{ from: null, to: null }];
        }
        setHours(updated);
    };

    const handleTimeChange = (dayIndex, slotIndex, type, newValue) => {
        const updated = [...hours];
        updated[dayIndex].slots[slotIndex][type] = newValue;
        setHours(updated);
    };

    const addTimeSlot = (index) => {
        const updated = [...hours];
        if (!Array.isArray(updated[index].slots)) {
            updated[index].slots = [];
        }
        updated[index].slots.push({ from: null, to: null });
        setHours(updated);
    };

    const removeTimeSlot = (dayIndex, slotIndex) => {
        const updated = [...hours];
        updated[dayIndex].slots.splice(slotIndex, 1);
        setHours(updated);
    };

    const handleSubmit = async () => {
        const selectedTimezone = timezone?.value || timezone;
        const payload = {
            vendor_id: currentVendorId,
            timezone: selectedTimezone,
            business_hours: hours
                .map((hour) => ({
                    day: hour.day,
                    is_open: hour.enabled,
                    intervals: hour.enabled ? hour.slots.map((slot) => ({
                        open_time:
              slot.from && dayjs(slot.from).isValid()
                  ? dayjs(slot.from).format('HH:mm')
                  : null,
                        close_time:
              slot.to && dayjs(slot.to).isValid()
                  ? dayjs(slot.to).format('HH:mm')
                  : null,
                    })) : [],
                })),
        };

        const url = `/business-hours/vendor/${currentVendorId}`;
        const method = isUpdating
            ? axiosVendorInstance.put
            : axiosVendorInstance.post;

        try {
            setLoading(true);
            await method(url, payload);
            setLoading(false);
            toast.success(
                `Business hours ${isUpdating ? 'updated' : 'submitted'} successfully!`
            );
        } catch (error) {
            setLoading(false);
            console.error('Error updating business hours:', error);
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        window.history.back();
    };

    const IOSSwitch = styled((props) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#65C466',
                    opacity: 1,
                    border: 0,
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#2ECA45',
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.grey[100],
                ...theme.applyStyles('dark', {
                    color: theme.palette.grey[600],
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7,
                ...theme.applyStyles('dark', {
                    opacity: 0.3,
                }),
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
            ...theme.applyStyles('dark', {
                backgroundColor: '#39393D',
            }),
        },
    }));

    return (
        <>
            {loading && <Loader />}
            <div className='vendor-header-footer'>
                <VendorTopMenuBar />
                <VendorSideMenuBar />
            </div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid
                    container
                    spacing={3}
                    justifyContent='center'
                    className='business-hour-container'
                >
                    <div className='business-hour-top'>
                        <div className='back-button11' onClick={() => goBack()}>
                            <div className='ico1'>
                                <MobileBackIcon />
                            </div>
                        </div>
                        <div className='business-hour-txt-layout'>
                            <h5 className='business-hour-txt'>Select Working Hours</h5>
                        </div>
                    </div>

                    <Grid
                        item
                        xs={12}
                        container
                        justifyContent='center'
                        alignItems='center'
                        style={{ gap: 10 }}
                    >
                        <div className='time-zone'>
                            <p className='time-zone-text'>Time zone</p>
                        </div>
                        <div style={{ width: '300px' }}>
                            <TimezoneSelect value={timezone} onChange={setTimezone} />
                        </div>
                    </Grid>

                    <TableContainer style={{ marginTop: 20, maxWidth: '600px' }}>
                        <Table className='business-hour-table'>
                            <TableBody>
                                {hours?.map((hour, dayIndex) => (
                                    <React.Fragment key={hour.day}>
                                        <TableRow
                                            sx={{
                                                '& td': { borderBottom: 'none', verticalAlign: 'top' },
                                            }}
                                        >
                                            <TableCell>
                                                {hour.day}
                                            </TableCell>
                                            <TableCell colSpan={3}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 10,
                                                    }}
                                                >
                                                    <IOSSwitch
                                                        checked={hour.enabled}
                                                        onChange={() => toggleDay(dayIndex)}
                                                    />

                                                    {!hour?.enabled && (
                                                        <span style={{ fontStyle: 'italic' }}>Closed</span>
                                                    )}
                                                </div>

                                                {hour?.enabled &&
                          hour?.slots?.map((slot, slotIndex) => (
                              <Grid
                                  container
                                  spacing={2}
                                  key={slotIndex}
                                  alignItems='center'
                                  style={{ marginTop: 10 }}
                              >
                                  <Grid item xs={5}>
                                      <TimePicker
                                          label='From'
                                          value={slot.from}
                                          onChange={(newValue) =>
                                              handleTimeChange(
                                                  dayIndex,
                                                  slotIndex,
                                                  'from',
                                                  newValue
                                              )
                                          }
                                          renderInput={(params) => (
                                              <TextField
                                                  {...params}
                                                  fullWidth
                                                  size='small'
                                              />
                                          )}
                                      />
                                  </Grid>
                                  <Grid item xs={5}>
                                      <TimePicker
                                          label='To'
                                          value={slot.to}
                                          onChange={(newValue) =>
                                              handleTimeChange(
                                                  dayIndex,
                                                  slotIndex,
                                                  'to',
                                                  newValue
                                              )
                                          }
                                          renderInput={(params) => (
                                              <TextField
                                                  {...params}
                                                  fullWidth
                                                  size='small'
                                              />
                                          )}
                                      />
                                  </Grid>
                                  <Grid item xs={2}>
                                      {hour.slots.length > 1 && (
                                          <IconButton
                                              onClick={() =>
                                                  removeTimeSlot(dayIndex, slotIndex)
                                              }
                                          >
                                              <DeleteIcon />
                                          </IconButton>
                                      )}
                                  </Grid>
                              </Grid>
                          ))}

                                                {hour.enabled && (
                                                    <Button
                                                        onClick={() => addTimeSlot(dayIndex)}
                                                        size='small'
                                                        style={{ marginTop: 10, color: 'var(--color4)', border: '1px solid var(--color4)' }}
                                                    >
                            + Add time slot
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Centered Button */}
                    <Grid item xs={12} container justifyContent='center'>
                        <Button
                            variant='contained'
                            className='business-hour-button'
                            onClick={handleSubmit}
                        >
                            {isUpdating ? 'Update' : 'Submit'}
                        </Button>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </>
    );
};

export default BusinessHours;
